import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import {
  FormControl,
  Grid,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  InputLabel,
  OutlinedInput,
  Typography,
  Box,
  Chip,
} from '@mui/material';
import { useApiGet } from '../../../utils/httpClientTs';
import { getLCChaptersList } from '../../../endpoints/chaptersTs';
import {
  LocalChapterList,
  LocalChapterListResponse,
} from '../../../types/chapters';
import SPDSelector from '../../../Components/SPDSelector';
import ServerSideTable, {
  ServerSideTableColumn,
} from '../../../Components/ServerSideTable';
import { SortOrder } from '../../../types/table';

const columns: ServerSideTableColumn<LocalChapterList>[] = [
  {
    id: 'abbreviation',
    firstCell: true,
    label: 'Abbreviation',
    width: '10%',
    sortable: true,
  },
  {
    id: 'name',
    label: 'Name',
    width: '30%',
    sortable: true,
  },
  {
    id: 'spd_chapter',
    label: 'State / Province / District',
    width: '30%',
    sortable: true,
  },
  {
    id: 'status',
    label: 'Status',
    width: '30%',
    sortable: true,
    render: (value: 'Active' | 'Disbanded' | 'Merged') => {
      const getStatusColor = (status: string) => {
        switch (status.toLowerCase()) {
          case 'active':
            return 'success';
          case 'merged':
            return 'warning';
          case 'disbanded':
            return 'error';
          default:
            return 'default';
        }
      };

      return (
        <Chip
          label={value}
          color={
            getStatusColor(value) as 'success' | 'warning' | 'error' | 'default'
          }
          size="small"
          variant="filled"
        />
      );
    },
  },
];

const LCChapterList: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [order, setOrder] = useState<SortOrder>('asc');
  const [orderBy, setOrderBy] = useState<string>('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [spd, setSpd] = useState(searchParams.get('spd') || '');
  const [status, setStatus] = useState(searchParams.get('status') || 'active');
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [delaySearch, setDelaySearch] = useState(
    searchParams.get('search') || ''
  );
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize component and set default status if not present
  useEffect(() => {
    if (!isInitialized) {
      const currentStatus = searchParams.get('status');
      if (!currentStatus) {
        // Set default status without adding to history
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.set('status', 'active');
        setSearchParams(newSearchParams, { replace: true });
      }
      setIsInitialized(true);
    }
  }, [isInitialized, searchParams, setSearchParams]);

  // Update search params when values change - consolidated to prevent multiple history entries
  useEffect(() => {
    if (!isInitialized) return; // Don't update until initialized

    const newSearchParams = new URLSearchParams(searchParams);

    // Update search
    if (search) {
      newSearchParams.set('search', search);
    } else {
      newSearchParams.delete('search');
    }

    // Update status
    if (status) {
      newSearchParams.set('status', status);
    } else {
      newSearchParams.delete('status');
    }

    // Update spd
    if (spd) {
      newSearchParams.set('spd', spd);
    } else {
      newSearchParams.delete('spd');
    }

    setSearchParams(newSearchParams);
  }, [search, status, spd, searchParams, setSearchParams, isInitialized]);

  const { data, isFetching } = useApiGet<LocalChapterListResponse>(
    ['local-chapters', rowsPerPage, page, spd, status, orderBy, order, search],
    getLCChaptersList,
    {
      enabled: true,
      retry: 1,
    },
    {
      rowsPerPage,
      page,
      spd,
      status,
      orderBy,
      order,
      search,
    }
  );

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSearch(delaySearch);
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [delaySearch]);

  const handleRowClick = (chapter: LocalChapterList) => {
    navigate(`/local-chapters/${chapter.id}`);
  };

  const handleSpdChange = (selectedSpd: any) => {
    if (!selectedSpd) {
      setSpd('');
    } else {
      setSpd(selectedSpd);
    }
  };

  const handleSort = (property: keyof LocalChapterList) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property as string);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0); // Reset to first page when changing rows per page
  };

  if (isFetching && !data) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <Typography variant="h6" color="text.secondary">
          Loading local chapters...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography variant="h4" color="primary" align="center" gutterBottom>
            Local Chapters
          </Typography>
        </Grid>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Search"
              id="search-field"
              size="small"
              value={delaySearch}
              onChange={(e) => setDelaySearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              placeholder="Search by name, abbreviation, or SPD..."
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <SPDSelector
              spd={spd}
              handleSpdSelect={handleSpdChange}
              reportId="local-chapter-list"
              all={true}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel id="status-label">Status</InputLabel>
              <Select
                labelId="status-label"
                id="status-select"
                value={status}
                input={<OutlinedInput label="Status" />}
                label="Status"
                onChange={(e) => setStatus(e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="disbanded">Disbanded</MenuItem>
                <MenuItem value="merged">Merged</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <ServerSideTable
            data={data?.data || []}
            columns={columns}
            loading={isFetching}
            page={page}
            rowsPerPage={rowsPerPage}
            totalCount={data?.pagination?.total_count || 0}
            totalPages={data?.pagination?.total_pages || 0}
            hasNext={data?.pagination?.has_next || false}
            hasPrev={data?.pagination?.has_prev || false}
            order={order}
            orderBy={orderBy}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            onSort={handleSort}
            onRowClick={handleRowClick}
            emptyMessage="No local chapters found"
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default LCChapterList;
